import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';
import BlogCTA from '../../components/BlogCTA';
import RelatedArticles from '../../components/RelatedArticles';

const schema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Article',
      'headline': 'How to Invoice International Clients (Step-by-Step)',
      'description': 'Currency choice, VAT/GST rules, payment methods, and exactly what to put on an invoice when billing clients overseas.',
      'url': 'https://www.invilic.com/blog/how-to-invoice-international-clients',
      'datePublished': '2025-05-20',
      'dateModified': '2025-05-20',
      'author': { '@type': 'Organization', 'name': 'Invilic', 'url': 'https://www.invilic.com' },
      'publisher': { '@type': 'Organization', 'name': 'Invilic', 'url': 'https://www.invilic.com' },
    },
    {
      '@type': 'FAQPage',
      'mainEntity': [
        {
          '@type': 'Question',
          'name': 'Should I charge VAT or GST on invoices to international clients?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Generally no. Most countries zero-rate or exempt services exported to foreign clients. In India, services to overseas clients are zero-rated for GST (no 18% GST applies). In the UK, services to non-UK businesses are outside the scope of UK VAT. In the EU, services to non-EU clients are generally VAT-exempt. Always confirm for your specific country and service type.' },
        },
        {
          '@type': 'Question',
          'name': 'What currency should I invoice overseas clients in?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Invoice in your client\'s preferred currency if possible — usually USD for US clients, GBP for UK clients, EUR for European clients. This removes the exchange rate burden from them and makes it easier for them to approve the invoice internally. Use a multi-currency tool and state the exact amount in the agreed currency.' },
        },
        {
          '@type': 'Question',
          'name': 'How do I handle exchange rate fluctuations on international invoices?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'The safest approach is to invoice in the currency of payment and receive payment in that currency, then convert using a service like Wise or your bank. If you invoice in a foreign currency but record income in your local currency, note the exchange rate used on the day of payment in your accounting records.' },
        },
        {
          '@type': 'Question',
          'name': 'What payment methods work best for international invoices?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Wise (formerly TransferWise) and Payoneer are the most cost-effective for freelancers — they offer local bank details in USD, EUR, GBP, and other currencies, so international clients pay as if they are paying a local bank. SWIFT wire transfer works for any currency but has higher fees. PayPal works but charges 3–5% in fees and unfavourable exchange rates.' },
        },
      ],
    },
  ],
};

const faqs = schema['@graph'][1].mainEntity;

export default function InvoiceInternationalClientsPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="How to Invoice International Clients (Step-by-Step) | Invilic Blog"
        description="Currency choice, VAT/GST rules for exports, payment methods, and exactly what to include when billing overseas clients."
        canonical="https://www.invilic.com/blog/how-to-invoice-international-clients"
        ogType="article"
        schema={schema}
      />
      <SeoNav />

      <main className="max-w-3xl mx-auto px-6 py-16">
        <header className="mb-10">
          <div className="flex items-center gap-2 mb-5 text-sm">
            <Link to="/blog" className="text-blue-600 dark:text-blue-400 hover:underline">Blog</Link>
            <ArrowRight className="w-3 h-3 text-gray-400" />
            <span className="text-gray-500 dark:text-gray-400">International</span>
          </div>
          <span className="inline-block bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 text-xs font-semibold px-2.5 py-1 rounded-full mb-4">International</span>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white leading-tight mb-4">
            How to Invoice International Clients (Step-by-Step)
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm">May 20, 2025 · 6 min read</p>
        </header>

        <article className="text-gray-700 dark:text-gray-300 leading-relaxed">

          <p className="text-lg mb-6">
            When invoicing international clients, invoice in their preferred currency (usually USD, GBP, or EUR), do not add your local tax (GST/VAT) since exports are generally zero-rated, and include your bank's SWIFT code or a service like Wise so the client knows exactly how to pay you. The process is simpler than most freelancers assume.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Step 1: Choose the Right Currency</h2>
          <p className="mb-4">
            The most important decision on an international invoice is which currency to use. The rule of thumb: invoice in the currency your client is most comfortable with, typically the currency of their country.
          </p>
          <ul className="space-y-2 mb-6 pl-1">
            {[
              'US clients → USD (makes approval easy through their finance team)',
              'UK clients → GBP (avoids exchange rate exposure for them)',
              'European clients → EUR (standard across the eurozone)',
              'Australian clients → AUD or USD (both are common)',
              'UAE clients → AED or USD (both are widely accepted)',
            ].map(item => (
              <li key={item} className="flex items-start gap-2 text-sm">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-500 flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
          <p className="mb-6">
            If you are unsure, ask the client which currency they prefer before starting work. Having this agreed upfront removes any ambiguity when the invoice arrives.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Step 2: Handle Tax Correctly — No GST or VAT on Exports</h2>
          <p className="mb-4">
            This is the part that confuses most freelancers. When you provide services to a foreign client, you generally do not charge your country's local tax — because it is an export of services.
          </p>
          <div className="space-y-3 mb-6">
            {[
              { country: 'India (GST)', rule: 'Services exported to foreign clients are zero-rated. You do not charge 18% GST. You can still claim ITC refunds on your input costs.' },
              { country: 'United Kingdom (VAT)', rule: 'Services to non-UK business clients are outside the scope of UK VAT. Do not add 20% VAT.' },
              { country: 'European Union (VAT)', rule: 'Services to non-EU clients are generally VAT-exempt. For EU B2B services, the reverse charge mechanism applies — the client accounts for VAT.' },
              { country: 'Pakistan / Bangladesh / Philippines', rule: 'Export of services to overseas clients is generally exempt from sales tax or VAT under the respective tax laws. Confirm with your local tax advisor.' },
            ].map(({ country, rule }) => (
              <div key={country} className="p-4 border border-gray-100 dark:border-gray-800 rounded-xl">
                <p className="font-semibold text-gray-900 dark:text-white text-sm mb-1">{country}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{rule}</p>
              </div>
            ))}
          </div>

          <BlogCTA
            text="Use the international invoice type on Invilic — zero tax by default, multi-currency support, SWIFT/IBAN fields included."
            to="/create/international"
            btnText="Create International Invoice Free"
          />

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Step 3: What to Include on an International Invoice</h2>
          <p className="mb-4">Beyond the standard invoice fields, international invoices should include a few extras:</p>
          <ul className="space-y-2 mb-6 pl-1">
            {[
              'Your bank name and account number (for wire transfers)',
              'SWIFT/BIC code — required for international bank transfers in any currency',
              'IBAN — required for transfers within Europe',
              'Wise / Payoneer account details — if you receive through these services',
              'Currency clearly stated (e.g., "USD 1,500.00" not just "1,500")',
              'Your country\'s equivalent of a tax registration number (if applicable)',
              'A note: "VAT/GST zero-rated — export of services" (optional but professional)',
            ].map(item => (
              <li key={item} className="flex items-start gap-2 text-sm">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-500 flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Step 4: Choose a Payment Method</h2>
          <p className="mb-4">How you receive the money is just as important as the invoice itself. Here are your main options:</p>
          <div className="overflow-x-auto mb-6">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-900/50">
                  <th className="text-left px-4 py-3 font-semibold text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800">Method</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800">Best for</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800">Fee</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { method: 'Wise', best: 'Any currency, best exchange rates', fee: '0.3–1.5%' },
                  { method: 'Payoneer', best: 'US clients, marketplace payments', fee: '1–3%' },
                  { method: 'SWIFT wire transfer', best: 'Large amounts, corporate clients', fee: '$15–50 flat' },
                  { method: 'PayPal', best: 'Small amounts, quick setup', fee: '3–5% + rate markup' },
                ].map(({ method, best, fee }) => (
                  <tr key={method} className="border-b border-gray-100 dark:border-gray-800">
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800">{method}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400 border border-gray-100 dark:border-gray-800">{best}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400 border border-gray-100 dark:border-gray-800">{fee}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Step 5: Set Clear Payment Terms</h2>
          <p className="mb-4">
            International clients often have longer internal approval processes. Setting clear payment terms — and following up — is essential:
          </p>
          <ul className="space-y-2 mb-6 pl-1">
            {[
              'Use "Net 15" or "Net 30" — avoid "Due on receipt" for larger clients as it creates friction',
              'Include a late payment clause: "Interest of 1.5% per month applies after the due date"',
              'Send the invoice the same day you complete the work — delays in sending cause delays in payment',
              'Follow up with a friendly email 2 days before the due date as a reminder',
            ].map(item => (
              <li key={item} className="flex items-start gap-2 text-sm">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-500 flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-6">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none text-sm">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-5 pb-4 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{acceptedAnswer.text}</p>
              </details>
            ))}
          </div>

          <BlogCTA
            text="Create a professional international invoice in USD, EUR, GBP, or any currency. SWIFT and IBAN fields included. Free."
            to="/create/international"
            btnText="Create International Invoice"
          />

        </article>
        <RelatedArticles current="/blog/how-to-invoice-international-clients" />
      </main>
    </div>
  );
}
